/**
 * Sentry integration (client)
 *
 * Centralizes Sentry initialization, user-context management, and PII scrubbing
 * for the browser app. Everything here is a no-op unless `VITE_SENTRY_DSN` is set,
 * so local development and preview builds without a DSN behave exactly as before.
 *
 * The rest of the app should import capture helpers from here (or from
 * `errorService`) rather than reaching for `@sentry/react` directly, so that the
 * "is Sentry enabled" gate lives in one place.
 */

import * as Sentry from '@sentry/react';
import type { User } from '../types';

const dsn = import.meta.env.VITE_SENTRY_DSN;

/** Whether Sentry is configured for this build. */
export const isSentryEnabled = Boolean(dsn);

/**
 * Release identifier used to group errors by deploy. Prefer an explicit
 * `VITE_SENTRY_RELEASE` (e.g. the git SHA injected at build time); fall back to
 * the app version baked in by Vite (see `vite.config.ts` `define`).
 */
const release =
  import.meta.env.VITE_SENTRY_RELEASE ||
  (typeof __APP_VERSION__ !== 'undefined' ? __APP_VERSION__ : undefined);

const environment =
  import.meta.env.VITE_SENTRY_ENVIRONMENT || import.meta.env.MODE || 'development';

/**
 * Keys that must never leave the browser. `beforeSend`/`beforeBreadcrumb`
 * strip these from any structured data Sentry is about to transmit.
 */
const SENSITIVE_KEYS = [
  'password',
  'passwd',
  'secret',
  'token',
  'apikey',
  'api_key',
  'authorization',
  'auth',
  'creditcard',
  'card',
  'cvv',
  'ssn',
];

function isSensitiveKey(key: string): boolean {
  const normalized = key.toLowerCase().replace(/[^a-z]/g, '');
  return SENSITIVE_KEYS.some((sensitive) => normalized.includes(sensitive));
}

/**
 * Redact unambiguous secret patterns from a free-text string (error messages,
 * breadcrumbs). Key-name scrubbing misses these because the secret lives inside
 * a message value, not under a sensitive key. Kept deliberately narrow so we
 * don't mangle ordinary error text.
 */
const SECRET_PATTERNS: RegExp[] = [
  /\b(?:sk|rk|pk)_(?:live|test)_[A-Za-z0-9]{8,}/g, // Stripe keys
  /\bBearer\s+[A-Za-z0-9._~+/-]+=*/gi, // Bearer tokens
  /\beyJ[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+/g, // JWTs
  /\bAKIA[0-9A-Z]{16}\b/g, // AWS access key ids
  /(X-Goog-Signature|Signature|GoogleAccessId|Expires|token)=[^&\s"']+/gi, // signed-URL params
];

export function redactSecrets(input: string): string {
  return SECRET_PATTERNS.reduce((acc, re) => acc.replace(re, '[Redacted]'), input);
}

/**
 * Recursively redact obviously-sensitive values from an arbitrary object before
 * it is sent to Sentry. Bounded in depth to avoid pathological cycles/costs.
 */
function scrub(value: unknown, depth = 0): unknown {
  if (depth > 6 || value === null || value === undefined) return value;
  if (Array.isArray(value)) return value.map((item) => scrub(item, depth + 1));
  if (typeof value === 'object') {
    const result: Record<string, unknown> = {};
    for (const [key, val] of Object.entries(value as Record<string, unknown>)) {
      result[key] = isSensitiveKey(key) ? '[Redacted]' : scrub(val, depth + 1);
    }
    return result;
  }
  if (typeof value === 'string') return redactSecrets(value);
  return value;
}

/**
 * Initialize Sentry. Safe to call unconditionally — it returns early when no DSN
 * is configured. Call as early as possible during app bootstrap.
 */
export function initSentry(): void {
  if (!isSentryEnabled) {
    return;
  }

  Sentry.init({
    dsn,
    environment,
    release,
    // Distributed tracing for page loads and outgoing requests. Kept modest to
    // control quota; tune per environment via the sample-rate envs.
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllText: true,
        blockAllMedia: true,
      }),
    ],
    tracesSampleRate: numberEnv(import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE, 0.1),
    // Session Replay: only record a small % of sessions, but always capture the
    // session leading up to an error.
    replaysSessionSampleRate: numberEnv(
      import.meta.env.VITE_SENTRY_REPLAYS_SESSION_SAMPLE_RATE,
      0.0,
    ),
    replaysOnErrorSampleRate: numberEnv(
      import.meta.env.VITE_SENTRY_REPLAYS_ON_ERROR_SAMPLE_RATE,
      1.0,
    ),
    // Restrict trace propagation headers to our OWN origin only (relative URLs).
    // The `sentry-trace` + `baggage` headers turn a request into a CORS-preflighted
    // one; Google's Firebase endpoints (identitytoolkit / securetoken /
    // firestore.googleapis.com) reject that preflight with 403 (no ACAO), which
    // breaks Auth token refresh and all Firestore reads — i.e. login dies. Never
    // propagate to googleapis.com / firebaseapp.com / run.app for this reason.
    tracePropagationTargets: [/^\//],
    sendDefaultPii: false,
    beforeSend(event) {
      if (event.request?.cookies) delete event.request.cookies;
      if (event.extra) event.extra = scrub(event.extra) as Record<string, unknown>;
      if (event.contexts) {
        event.contexts = scrub(event.contexts) as typeof event.contexts;
      }
      // Exception messages are not covered by the key-based scrubber; redact
      // secret-looking substrings (e.g. Stripe keys, signed-URL tokens).
      for (const value of event.exception?.values ?? []) {
        if (value.value) value.value = redactSecrets(value.value);
      }
      if (event.message) event.message = redactSecrets(event.message);
      return event;
    },
    beforeBreadcrumb(breadcrumb) {
      if (breadcrumb.data) {
        breadcrumb.data = scrub(breadcrumb.data) as Record<string, unknown>;
      }
      if (breadcrumb.message) breadcrumb.message = redactSecrets(breadcrumb.message);
      return breadcrumb;
    },
    // Noise we never want to alert on.
    ignoreErrors: [
      'ResizeObserver loop limit exceeded',
      'ResizeObserver loop completed with undelivered notifications.',
      'Non-Error promise rejection captured',
      // Firebase surfaces transient network blips as these; not actionable.
      'Failed to fetch',
      'NetworkError when attempting to fetch resource.',
      'Load failed',
    ],
  });
}

/**
 * Associate the current signed-in user + active organization with subsequent
 * events. Only non-PII identifiers are attached (no name; email is opt-in).
 */
export function setSentryUser(user: User | null): void {
  if (!isSentryEnabled) return;

  if (!user) {
    Sentry.setUser(null);
    Sentry.setTag('organizationId', undefined);
    return;
  }

  Sentry.setUser({
    id: user.id,
    // Email helps triage but is PII — keep it, Sentry treats `user` specially and
    // it is scoped to the org's own Sentry project.
    email: user.email,
  });
  Sentry.setTag('organizationId', user.activeOrganizationId ?? undefined);
  // Roles for the active org (falls back to the legacy flat roles array).
  const activeRoles =
    (user.activeOrganizationId && user.orgRoles?.[user.activeOrganizationId]) ||
    user.roles ||
    [];
  Sentry.setTag('userRoles', activeRoles.join(',') || undefined);
}

/** Clear user context on sign-out. */
export function clearSentryUser(): void {
  if (!isSentryEnabled) return;
  Sentry.setUser(null);
}

function numberEnv(raw: string | undefined, fallback: number): number {
  if (raw === undefined || raw === '') return fallback;
  const parsed = Number(raw);
  return Number.isFinite(parsed) ? parsed : fallback;
}

// Re-export the pieces the rest of the app needs so callers depend on this
// module (which owns the enabled-gate) rather than `@sentry/react` directly.
export { Sentry };
export const captureException = Sentry.captureException;
export const captureMessage = Sentry.captureMessage;
export const addBreadcrumb = Sentry.addBreadcrumb;
